/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { Card, Row, Col, message, Button, Input, Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  DropDownComponent,
  TableComponent,
  CalendarComponent,
  RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadInscanReport } from "../../../../utils/downloadUtils";
import {
  SERVICE_TYPE_OPTIONS,
  DOCUMENT_TYPE_OPTIONS,
  BOOKING_TYPE_OPTIONS
} from "../../../../constants/reportConstants";
import search_icon from "../../../../assests/images/search-icon.svg";

const InscanReport = () => {
  // Global request cancellation hook
  const { createNewRequest, isLatestRequest } = useRequestCancellation();

  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [dateRange, setDateRange] = useState(() => [
    moment().subtract(1, "days"),
    moment()
  ]);
  const [hubList, setHubList] = useState([]);
  const [selectedPremiseIds, setSelectedPremiseIds] = useState([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState([]);
  const [selectedBookingTypes, setSelectedBookingTypes] = useState([]);
  const [awbSearch, setAwbSearch] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });
  const [reportData, setReportData] = useState([]);
  const [hubUser, setHubUser] = useState();
  const [lastsynced, setLastsynced] = useState(null);
  const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);


  // Initial load - fetch hub list
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const hubs = await getHubList();
        setHubList(hubs);
      } catch (error) {
        message.error(error?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Set default hub for HUB_ADMIN users
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
    const userId = JSON.parse(localStorage.getItem("userId") || "{}");
    setHubUser(user?.roles?.[0]);

    const activeHub = JSON.parse(localStorage.getItem("active_hub"));
    const activeHubId = activeHub?.hubId;

    if (hubList.length > 0) {
      if (activeHubId) {
        const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
        if (defaultHub) {
          setSelectedPremiseIds([defaultHub.value]);
        }
      } else if (user?.roles?.[0] === "ROLE_HUBADMIN") {
        const defaultHub = hubList.find(hub => String(hub.value) === String(userId?.userInfo?.premiseId));
        if (defaultHub) {
          setSelectedPremiseIds([defaultHub.value]);
        }
      }
    }
  }, [hubList]);

  // Debounced effect for report fetching
  useEffect(() => {
    if (!dateRange || !dateRange.length === 2 || !dateRange[0] || !dateRange[1]) {
      setReportData([]);
      setPagination(prev => ({ ...prev, total: 0 }));
      return;
    }

    const timer = setTimeout(() => {
      fetchReportData();
    }, 300);

    return () => clearTimeout(timer);
  }, [dateRange, selectedPremiseIds, selectedServiceTypes, selectedDocumentTypes, selectedBookingTypes, awbSearch, pagination.current, pagination.pageSize]);

  const fetchReportData = async () => {
    setReportData([]);
    // Early return if date range is not set
    if (!dateRange || !dateRange.length === 2 || !dateRange[0] || !dateRange[1]) {
      setReportData([]);
      setPagination(prev => ({ ...prev, total: 0 }));
      return;
    }

    // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
    const { signal, requestId } = createNewRequest("inscan-report");

    try {
      setLoading(true);
      const params = {
        fromDate: dateRange[0].format("YYYY-MM-DD"),
        toDate: dateRange[1].format("YYYY-MM-DD"),
        premiseIds: selectedPremiseIds.length > 0 ? selectedPremiseIds : undefined,
        serviceTypes: selectedServiceTypes.length > 0 ? selectedServiceTypes : undefined,
        documentTypes: selectedDocumentTypes.length > 0 ? selectedDocumentTypes : undefined,
        bookingTypes: selectedBookingTypes.length > 0 ? selectedBookingTypes : undefined,
        awbSearch: awbSearch.trim() || undefined,
        page: (pagination.current || 1) - 1,
        size: pagination.pageSize || 10
      };

      const response = await ReportsService.getInscanReport(params, {
        signal
      });

      // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
      if (isLatestRequest(requestId)) {
        setReportData(response.data || []);
        setLastsynced(response?.lastRefreshedAt);
        setNextScheduledRefresh(response?.nextScheduledRefresh);
        setPagination(prev => ({
          ...prev,
          total: response.total || 0
        }));
      }

    } catch (error) {
      // 🔥 DON"T UPDATE STATE IF REQUEST WAS CANCELLED
      if (error.name === "AbortError") {
        console.log("🔥 Request was cancelled at HTTP level - no state update");
        return;
      }

      // Only update if this is still the latest request
      if (isLatestRequest(requestId)) {
        message.error(error?.message);
        setReportData([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
      }
    } finally {
      // 🔥 ONLY UPDATE LOADING IF THIS IS THE LATEST REQUEST
      if (isLatestRequest(requestId)) {
        setLoading(false);
      }
    }
  };

  const handleDownload = async () => {
    try {
      setDownloadLoading(true);
      const params = {
        fromDate: dateRange[0].format("YYYY-MM-DD"),
        toDate: dateRange[1].format("YYYY-MM-DD"),
        premiseIds: selectedPremiseIds.length > 0 ? selectedPremiseIds : undefined,
        serviceTypes: selectedServiceTypes.length > 0 ? selectedServiceTypes : undefined,
        documentTypes: selectedDocumentTypes.length > 0 ? selectedDocumentTypes : undefined,
        bookingTypes: selectedBookingTypes.length > 0 ? selectedBookingTypes : undefined,
        awbSearch: awbSearch.trim() || undefined
      };

      await downloadInscanReport(params);
      message.success("Download started successfully");
    } catch (error) {
      message.error("Failed to download report");
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleTableChange = (paginationInfo) => {
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    }));
  };

  const columns = [
    {
      title: "AWB Number",
      dataIndex: "awbNumber",
      key: "awbNumber",
      width: 150,
      fixed: "left",
      render: (text, record) => (
        <span
          style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
          onClick={() => {
            const url = `/shipment-tracker?awb=${text}`;
            window.open(url, "_blank");
          }}
        >
          {text}
        </span>
      )
    },
    {
      title: "Operation Date Time",
      dataIndex: "operationDateTime",
      key: "operationDateTime",
      width: 180,
      render: (text) => moment(text).format("DD/MM/YYYY HH:mm:ss")
    },
    {
      title: "Hub Name",
      dataIndex: "premiseName",
      key: "premiseName",
      width: 200
    },
    //bookingCp
    {
      title: "Booking CP",
      dataIndex: "bookingCp",
      key: "bookingCp",
      width: 150
    },
    {
      title: "Booking Type",
      dataIndex: "bookingType",
      key: "bookingType",
      width: 130
    },
    {
      title: "Service Type",
      dataIndex: "serviceType",
      key: "serviceType",
      width: 150
    },
    {
      title: "Travel By",
      dataIndex: "travelBy",
      key: "travelBy",
      width: 120
    },
    {
      title: "Doc Type",
      dataIndex: "docType",
      key: "docType",
      width: 120
    },
    {
      title: "User Email ID",
      dataIndex: "userEmailId",
      key: "userEmailId",
      width: 250
    },

  ];

  return (
    <div className="report-container">
      <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
        <Heading title="Inscan Shipments Report">
          <div style={{ paddingTop: "5px" }}>
            <RefreshTimestamp
              lastRefreshedAt={lastsynced}
              nextScheduledRefresh={nextScheduledRefresh}
            />
          </div>
        </Heading>
      </div>
      <Card className="report-card">
        <Row gutter={[16, 16]} className="filters-section">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Date Range</div>
            <CalendarComponent
              className="custom-calendar"
              type="range"
              value={dateRange}
              onChange={(value) => {
                setDateRange(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">HUB Name</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder="Select Hubs"
              options={hubList}
              onChange={(value) => {
                setSelectedPremiseIds(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedPremiseIds}
              mode={hubUser !== "ROLE_HUBADMIN" ? "multiple" : "default"}
              allowClear
              maxTagCount="responsive"
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
              disabled={hubUser === "ROLE_HUBADMIN"}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Service Types</div>
            <DropDownComponent
              placeholder="Select Service Types"
              options={SERVICE_TYPE_OPTIONS}
              onChange={(value) => {
                setSelectedServiceTypes(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedServiceTypes}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Document Type</div>
            <Select
              className="small-dropdown"
              style={{ width: "100%" }}
              placeholder="Select Document Type"
              value={selectedDocumentTypes.length > 0 ? selectedDocumentTypes : undefined}
              onChange={(value) => {
                setSelectedDocumentTypes(value ? [value] : []);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              allowClear
              options={DOCUMENT_TYPE_OPTIONS}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Booking Type</div>
            <Select
              className="small-dropdown"
              style={{ width: "100%" }}
              placeholder="Select Booking Type"
              value={selectedBookingTypes.length > 0 ? selectedBookingTypes : undefined}
              onChange={(value) => {
                setSelectedBookingTypes(value ? [value] : []);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              allowClear
              options={BOOKING_TYPE_OPTIONS}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">AWB Search</div>
            <Input
              className="mr-1 small-dropdown"
              placeholder="Enter AWB numbers (comma separated)"
              value={awbSearch}
              onChange={(e) => {
                setAwbSearch(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              bordered
              style={{
                borderRadius: "5px",
                height: "40px",
              }}
              maxLength={200}
              suffix={<img src={search_icon} alt="search" />}
              onKeyPress={(e) => {
                const forbiddenChars = /[@!$#%^&*=+()]/;
                if (forbiddenChars.test(e.key)) {
                  e.preventDefault();
                  message.destroy();
                }
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={handleDownload}
              loading={downloadLoading}
              icon={<DownloadOutlined />}
              disabled={!dateRange || !dateRange[0] || !dateRange[1]}
            >
              Download Report
            </Button>
          </Col>
        </Row>

        <div className="table-section">
          <TableComponent
            columns={columns}
            dataSource={reportData}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey="awbNumber"
            scroll={{ x: 1200 }}
          />
        </div>
      </Card>
    </div>
  );
};

export default InscanReport;
